import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMutation } from "@apollo/client";
import Button from "../../components/Button/Button";
import { REGISTER_USER_WITH_LINK } from "../Signin/query";
import { Typography, Grid, Box, Stack } from "@mui/material";
import { clearLocalStorageExcept, deleteCookie, splitMessageBybraces } from "../../Services/helper";
import { LoginFooter } from "../../components/shared/Login/LoginFooter";
import { CenterStack, PageGrid, StyledCard } from "../../Styles/GlobalStyles";
import SuccessDialogBox from "../Home/Components/NetworkStatus/SuccessDialogBox";
import { LoadingSpinner } from "../../components/shared/LoadingSpinner/LoadingSpinner";
import { UpdateErrorComponent } from "../../components/shared/UpdateErrorComponent/UpdateErrorComponent";
import { useNavigate } from "react-router-dom";
import { auth } from "../../Config/firebase";
import { signOut } from "firebase/auth";
import { ENABLE_BUSINESS_PORTAL } from "../../Constants/constants";

export const AccountNotFound = () => {
  const isAdmin = process.env.REACT_APP_CXP_ADMIN_ACCESS_ENABLED;
  const userError = useSelector((state: any) => state?.userInfo?.error);
  const { userNotInFirestore } = useSelector((state: any) => state?.userInfo);
  const errorMessage = splitMessageBybraces(userError);
  const impersonatedEmail = localStorage.getItem("impersonatedEmail");
  const [emailSent, setEmailSent] = React.useState(false);
  const [emailNotSent, setEmailNotSent] = React.useState(false);
  const [openLoader, setOpenLoader] = React.useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const handleOpenLoader = () => {
    setOpenLoader(true);
  };
  const handleCloseLoader = () => {
    setOpenLoader(false);
  };
  const handleSignin = () => {
    handleOpenLoader();
    setTimeout(async () => {
      await signOut(auth)
        .then(() => {
          dispatch({ type: "USER_LOGOUT" });
          clearLocalStorageExcept(["myAccountOnly"]);
          sessionStorage.clear();
          deleteCookie("Token");
          deleteCookie("customerUSI");
          handleCloseLoader();
          navigate("/register");
        })
        .catch((err) => {
          handleCloseLoader();
          throw new Error(err);
        });
    }, 3000);
  };

  const [registerUserWithLink, { loading: registerLinkLoading }] = useMutation(
    REGISTER_USER_WITH_LINK,
    {
      fetchPolicy: "network-only",
      onCompleted: () => setEmailSent(true),
      onError: () => setEmailNotSent(true),
    }
  );

  const handleSendEmail = async () => {
    try {
      const response = await registerUserWithLink({
        variables: {
          email: impersonatedEmail,
        },
      });
      if (response?.data) {
        if (!response?.data?.registerUserWithLink?.error) {
          setEmailSent(true);
        } else if (response?.data?.registerUserWithLink?.error) {
          setEmailNotSent(true);
        }
      }
    } catch (err) {
      console.log(err);
    }
  };

  const handleFontWeight = (message: any) => {
    if (
      message?.toLowerCase()?.includes("profile not") ||
      message?.toLowerCase()?.includes("not found in dynamics")
    ) {
      return 600;
    } else {
      return 400;
    }
  };

  const handleFontSize = (message: any) => {
    if (
      message?.toLowerCase()?.includes("profile not") ||
      message?.toLowerCase()?.includes("not found in dynamics")
    ) {
      return "20px";
    } else {
      return "16px";
    }
  };

  return (
    <Box>
      <PageGrid container sx={{ paddingBottom: "2rem !important" }}>
        <Grid item xs={12}>
          <StyledCard sx={{ py: 12, mt: 2 }}>
            <CenterStack marginY={"1.2rem"} data-testid="refresh-page" gap={2}>
              {ENABLE_BUSINESS_PORTAL ? (
                <>
                  <Box textAlign="center" py={6}>
                    <Stack spacing={2} alignItems="center">
                      <Typography
                        variant="h6"
                        fontWeight={600}
                        data-testid="account-not-found-title">
                        Account Not Found
                      </Typography>

                      <Typography
                        variant="body1"
                        color="textSecondary"
                        data-testid="account-not-found-subtitle">
                        We couldn't find any account associated with your credentials.
                      </Typography>

                      <Button type="payment" title="SIGN UP" onClick={handleSignin} />
                    </Stack>
                  </Box>
                </>
              ) : errorMessage?.length > 0 ? (
                <>
                  {isAdmin === "FALSE" && !userNotInFirestore && (
                    <Typography component={"p"} textAlign={"center"} data-testid="error-message">
                      Something went wrong !
                    </Typography>
                  )}

                  {errorMessage?.map((message: string) => (
                    <Typography
                      key={message}
                      fontWeight={() => handleFontWeight(message)}
                      component={"p"}
                      textAlign={"center"}
                      fontSize={() => handleFontSize(message)}
                      data-testid="error-message">
                      {message}
                    </Typography>
                  ))}

                  {isAdmin === "TRUE" && (
                    <Button type="payment" title="SEND EMAIL" onClick={handleSendEmail} />
                  )}
                </>
              ) : (
                <Typography component={"p"} textAlign={"center"} data-testid="error-message">
                  Something went wrong ! please try again.
                </Typography>
              )}
            </CenterStack>
          </StyledCard>
          <Stack justifyContent={"center"} alignItems={"center"}>
            <LoginFooter />
          </Stack>
        </Grid>
      </PageGrid>
      {registerLinkLoading && <LoadingSpinner />}
      <>
        <SuccessDialogBox
          open={emailSent}
          onClose={() => setEmailSent(false)}
          heading="An email has been successfully sent to the user."
          message="Please ask the user to check their inbox and register an account."
        />
      </>
      <UpdateErrorComponent openError={emailNotSent} setOpenError={setEmailNotSent} />
      {openLoader && <LoadingSpinner />}
    </Box>
  );
};
